import { MarketingPermissions } from "@redotech/account-common/permissions";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoOrderTrackingRpcClientContext } from "@redotech/redo-merchant-app-common/order-tracking-rpc-client-provider";
import { folderTypes } from "@redotech/redo-views-common/folders";
import { OrderTrackingShipmentView } from "@redotech/redo-views-common/views";
import { toast } from "@redotech/redo-web/alert";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface OrderTrackingShipmentViewsData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: OrderTrackingShipmentView[],
    updatedFolders: folderTypes["order-tracking-shipments"][],
  ) => void;
  hasInitiallyLoaded: boolean;
  views: OrderTrackingShipmentView[];
  folders: folderTypes["order-tracking-shipments"][];
}

export const OrderTrackingShipmentViewsContext = createContext<
  OrderTrackingShipmentViewsData | undefined
>(undefined);

export const ReloadOrderTrackingShipmentViewsContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

export const OrderTrackingShipmentViewsProvider = memo(
  function OrderTrackingShipmentViewsProvider({
    children,
  }: {
    children: ReactNode | ReactNode[];
  }) {
    const team = useRequiredContext(TeamContext);
    const client = useContext(RedoOrderTrackingRpcClientContext);

    const [views, setViews] = useState<OrderTrackingShipmentView[]>([]);
    const [folders, setFolders] = useState<
      folderTypes["order-tracking-shipments"][]
    >([]);
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

    const hasPermission = useHasPermission(
      MarketingPermissions.VIEW_ORDER_TRACKING_ANALYTICS,
    );

    const [_, loadViews] = useTriggerLoad(async (signal) => {
      if (!client || !hasPermission) {
        return;
      }

      const result = await client.getOrderTrackingViewsAndFolders(null, {
        signal,
      });
      setHasInitiallyLoaded(true);
      setViews(result.views);
      setFolders(result.folders);
    });

    const reloadViews = useHandler(async () => {
      loadViews();
    });

    useEffect(() => {
      void reloadViews();
    }, [client, reloadViews, team, hasPermission]);

    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showEditFolderModal, setShowEditFolderModal] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<
      folderTypes["order-tracking-shipments"] | null
    >(null);

    const handleCreateFolder = async (name: string) => {
      if (!client) {
        return;
      }
      try {
        await client.createOrderTrackingViewFolder({ name });
        void reloadViews();
      } catch (error) {
        console.error("Error creating folder", error);
        toast("Failed to create folder", { variant: "error" });
        throw error;
      }
    };

    const handleEditFolder = async (folderId: string, folderName: string) => {
      if (!client) {
        return;
      }
      try {
        await client.updateOrderTrackingViewFolder({
          folderId,
          name: folderName,
        });
        void reloadViews();
      } catch (error) {
        console.error("Error updating folder", error);
        toast("Failed to update folder", { variant: "error" });
        throw error;
      }
    };

    const handleDeleteFolder = async (folderId: string) => {
      if (!client) {
        return;
      }
      try {
        await client.deleteOrderTrackingViewFolder({ folderId });
        void reloadViews();
      } catch (error) {
        console.error("Error deleting folder", error);
        toast("Failed to delete folder", { variant: "error" });
        throw error;
      }
    };

    const updateOrdering = async (
      updatedViews: OrderTrackingShipmentView[],
      updatedFolders: folderTypes["order-tracking-shipments"][],
    ) => {
      if (!client) {
        return;
      }
      setViews(updatedViews);
      setFolders(updatedFolders);

      try {
        await client.saveOrderTrackingViewsAndFolders({
          views: updatedViews,
          folders: updatedFolders,
        });
      } catch (error) {
        console.error("Error saving view and folder ordering", error);
        toast("Failed to save view ordering", { variant: "error" });
        void reloadViews();
      }
    };

    const openCreateFolderModal = useHandler(() => {
      setShowCreateFolderModal(true);
    });

    const openEditFolderModal = useHandler((folderId: string) => {
      const folder = folders?.find((f) => f._id === folderId);
      if (folder) {
        setShowEditFolderModal(true);
        setSelectedFolder(folder);
      }
    });

    const reservedFolderNames = useMemo(() => {
      return new Set(folders.map((f) => f.name));
    }, [folders]);

    return (
      <OrderTrackingShipmentViewsContext.Provider
        value={{
          openCreateFolderModal,
          openEditFolderModal,
          handleDeleteFolder,
          updateOrdering,
          views,
          folders,
          hasInitiallyLoaded,
        }}
      >
        <ReloadOrderTrackingShipmentViewsContext.Provider value={reloadViews}>
          {children}
          <CreateFolderModal
            isOpen={showCreateFolderModal}
            onClose={() => setShowCreateFolderModal(false)}
            onSubmit={handleCreateFolder}
            reservedFolderNames={reservedFolderNames}
          />
          {selectedFolder && (
            <EditFolderModal
              deleteFolder={handleDeleteFolder}
              isOpen={showEditFolderModal}
              onClose={() => setShowEditFolderModal(false)}
              onSubmit={handleEditFolder}
              reservedFolderNames={reservedFolderNames}
              selectedFolder={selectedFolder}
            />
          )}
        </ReloadOrderTrackingShipmentViewsContext.Provider>
      </OrderTrackingShipmentViewsContext.Provider>
    );
  },
);

const CreateFolderModal = memo(function CreateFolderModal({
  isOpen,
  onClose,
  onSubmit,
  reservedFolderNames,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (folderName: string) => Promise<void>;
  reservedFolderNames: Set<string>;
}) {
  const [folderName, setFolderName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(folderName);
      onClose();
    } finally {
      setIsSubmitting(false);
      setFolderName("");
    }
  };

  const primaryButton: RedoModalButtonProps = {
    text: "Create",
    onClick: handleSubmit,
    disabled:
      !folderName.trim().length || reservedFolderNames.has(folderName.trim()),
  };

  const secondaryButton: RedoModalButtonProps = {
    text: "Cancel",
    onClick: onClose,
    disabled: isSubmitting,
    pending: false,
  };

  return (
    <RedoModal
      buttonPlacement="full"
      isOpen={isOpen}
      onModalCloseRequested={isSubmitting ? () => {} : onClose}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      title="Create folder"
    >
      <Flex dir="column" gap="xl">
        <Flex dir="column">
          <Text>Folder name</Text>
          <RedoTextInput
            setValue={setFolderName}
            size="md"
            state={isSubmitting ? "readonly" : "default"}
            value={folderName}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});

const EditFolderModal = memo(function EditFolderModal({
  isOpen,
  onClose,
  onSubmit,
  selectedFolder,
  deleteFolder,
  reservedFolderNames,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (folderId: string, folderName: string) => Promise<void>;
  selectedFolder: folderTypes["order-tracking-shipments"];
  deleteFolder: (folderId: string) => Promise<void>;
  reservedFolderNames: Set<string>;
}) {
  const [folderName, setFolderName] = useState(selectedFolder.name);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const submitDisabled =
    !folderName.trim().length || reservedFolderNames.has(folderName.trim());

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(selectedFolder._id, folderName);
      onClose();
    } finally {
      setIsSubmitting(false);
      setFolderName("");
    }
  };

  const primaryButton: RedoModalButtonProps = {
    text: "Save",
    onClick: handleSubmit,
    disabled: !!submitDisabled,
    pending: false,
  };

  const secondaryButton: RedoModalButtonProps = {
    text: "Delete",
    onClick: () => setShowConfirmDelete(true),
    disabled: false,
    theme: "destructive",
  };

  return (
    <>
      <RedoModal
        buttonPlacement="full"
        isOpen={isOpen}
        onModalCloseRequested={isSubmitting ? () => {} : onClose}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        title="Edit folder"
      >
        <Flex dir="column" gap="xl">
          <Flex dir="column">
            <Text>Folder name</Text>
            <RedoTextInput
              setValue={setFolderName}
              size="md"
              state={isSubmitting ? "readonly" : "default"}
              value={folderName}
            />
          </Flex>
        </Flex>
      </RedoModal>
      <RedoModal
        isOpen={showConfirmDelete && !!selectedFolder}
        onModalCloseRequested={() => setShowConfirmDelete(false)}
        primaryButton={{
          text: "Delete",
          onClick: async () => {
            if (selectedFolder) {
              await deleteFolder(selectedFolder._id);
              onClose();
            }
          },
          theme: "destructive",
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setShowConfirmDelete(false),
        }}
        theme="error"
        title="Confirm delete"
      >
        <Text>
          Are you sure you want to delete the folder "{selectedFolder.name}"?
        </Text>
      </RedoModal>
    </>
  );
});
